import {
    MediaProgramWithGuides,
    MediaProgramMetadata,
} from '@/utils/media/types';
import { EnumButtonName, EventProperties } from '@calm/evexia-analytics';
import { RbcCircleRec, WorkflowWorkflowInfo } from '@calm/evexia-web-sdk';
import { EntireCardButtonProps } from '@rhg/components/card';
import { SxProps, Theme } from '@rhg/libs/material';
import { ValueByBreakpoint } from '@rhg/libs/material/sx';
import { ProgramProvider } from '@rhg/providers/program';
import { ReactNode } from 'react';
import { CardVariant } from '../card/types';
import { ProgramContextAwareProgramCard } from './program-context-aware';

export const styleClinicalBadgeIcon = {
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: 'info.main',
    color: (theme: Theme) =>
        theme.palette.mode === 'light' ? 'white' : 'black',
};

export const styleCarouselContent = {
    minHeight: 152,
    justifyContent: 'flex-start',
};

export interface ProgramCardProps {
    variant: CardVariant;
    titleComponent?: React.ElementType;
    program: MediaProgramWithGuides;
    packClass?: string;
    packProgram?: MediaProgramMetadata;
    recommendation?: RbcCircleRec;
    workflow?: WorkflowWorkflowInfo;
    /** Hides the overlay icons (session, completed status, etc.) on the image. Doesn't hide the focus/hover overlay */
    hideImageOverlays?: boolean;
    isTopRecommendation?: boolean;
    displayGuideProgress?: boolean;
    imageForcedSize?: number | string;
    /**
     * This prop affects only the carousel variant cards. It specifies whether or not
     * the category description is going to be displayed.
     */
    isCarouselCategoryDescriptionDisplayed?: boolean;
    isContributorAvatarDisplayedOnCarousel?: boolean;
    carouselInProgressTitleMaxLines?: number;
    /** Provides information about the type/category of content (particularly useful in DTC content) */
    contentType?: string;
    additionalAction?: React.ReactNode;
    /** An action tied to the program workflow, either join or restart.
     * If this is defined, the enhanced banner actions are displayed, where
     * the first action triggers this function.
     */
    joinAction?: () => void;
    isNotClickable?: boolean;
    sx?: SxProps<Theme>;
    contentSx?: SxProps;
    imageWidth?: Partial<ValueByBreakpoint<number | string>>;
    imageIconTop?: ReactNode;
    imageSideRadiusEnabled?: boolean;
    /** Eager-load + high fetch priority for this card's image. Set only for the
     *  above-the-fold/LCP card (e.g. the first item of a visible carousel). */
    priority?: boolean;
    trackButton?: EnumButtonName;
    trackProps?: EventProperties;
    entireCardButtonProps?: EntireCardButtonProps;
    role?: string;
    'aria-label'?: string;
    shouldShowSessionLength?: boolean;
    contentFeedbackId?: string;
    contentTagOverride?: string;
    showNarratorAvatar?: boolean;
    /**
     * When true, clicking the card both opens the guide player with the
     * current/next session AND navigates to the program details page. Used
     * for entry points (e.g., end-of-journaling recommendation) where the
     * user should jump straight into playback on the program page in a
     * single click.
     */
    playCurrentSessionOnClick?: boolean;
}
export function ProgramCard({
    program,
    workflow,
    packClass,
    packProgram,
    contentFeedbackId,
    recommendation,
    ...props
}: ProgramCardProps) {
    return (
        <ProgramProvider
            program={program}
            packClass={packClass}
            workflow={workflow}
            packProgram={packProgram}
            contentFeedbackId={contentFeedbackId}
            recommendation={recommendation}
        >
            <ProgramContextAwareProgramCard {...props} />
        </ProgramProvider>
    );
}
